import { useEffect, useMemo, useRef } from 'react'

import { cn } from '#/shared/lib/utils'
import { ChevronDownIcon } from '#/shared/ui/icons'
import { Popover } from '#/shared/ui/popover'

export interface TimePickerProps {
  value?: string
  onChange: (value: string) => void
  /** Fires when the panel opens/closes — use it to commit on close. */
  onOpenChange?: (open: boolean) => void
  placeholder?: string
  size?: 'default' | 'sm'
  error?: boolean
  className?: string
  portal?: boolean
}

type Period = 'AM' | 'PM'

interface TimeParts {
  hour12: number
  minute: number
  period: Period
}

const DEFAULT_PARTS: TimeParts = { hour12: 12, minute: 0, period: 'AM' }

const SIZE_CLASSES = {
  default: 'h-12 px-2.5 text-base',
  sm: 'h-8 px-2 text-sm',
} as const

interface ColumnItem {
  value: string
  label: string
}

const HOUR_ITEMS: ColumnItem[] = Array.from({ length: 12 }, (_, index) => {
  const hour = index + 1
  return { value: String(hour), label: String(hour) }
})

const MINUTE_ITEMS: ColumnItem[] = Array.from({ length: 60 }, (_, minute) => ({
  value: String(minute),
  label: pad(minute),
}))

const PERIOD_ITEMS: ColumnItem[] = [
  { value: 'AM', label: 'AM' },
  { value: 'PM', label: 'PM' },
]

function pad(value: number): string {
  return value.toString().padStart(2, '0')
}

function parseParts(value: string | undefined): TimeParts | null {
  if (!value) return null
  const [hourPart, minutePart] = value.split(':')
  const hour = Number(hourPart)
  const minute = Number(minutePart)
  if (Number.isNaN(hour) || Number.isNaN(minute)) return null
  return {
    hour12: hour % 12 === 0 ? 12 : hour % 12,
    minute,
    period: hour < 12 ? 'AM' : 'PM',
  }
}

function composeValue({ hour12, minute, period }: TimeParts): string {
  const base = hour12 % 12
  const hour24 = period === 'PM' ? base + 12 : base
  return `${pad(hour24)}:${pad(minute)}`
}

export function formatTimeLabel(value: string): string {
  const parts = parseParts(value)
  if (!parts) return value
  return `${parts.hour12}:${pad(parts.minute)} ${parts.period}`
}

function TimeColumn({
  items,
  selected,
  onSelect,
}: {
  items: ColumnItem[]
  selected: string
  onSelect: (value: string) => void
}) {
  const listRef = useRef<HTMLDivElement>(null)
  const selectedRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const list = listRef.current
    const node = selectedRef.current
    if (!list || !node) return
    list.scrollTop =
      node.offsetTop - list.clientHeight / 2 + node.clientHeight / 2
  }, [])

  return (
    <div
      ref={listRef}
      className="flex flex-1 flex-col gap-0.5 overflow-y-auto no-scrollbar"
    >
      {items.map((item) => (
        <button
          key={item.value}
          ref={item.value === selected ? selectedRef : undefined}
          type="button"
          onClick={() => onSelect(item.value)}
          className={cn(
            'flex h-8 w-full shrink-0 cursor-pointer items-center justify-center rounded-sm text-sm text-gray-900 hover:bg-gray-100',
            item.value === selected && 'bg-gray-100 font-medium text-primary',
          )}
        >
          {item.label}
        </button>
      ))}
    </div>
  )
}

function TimePanel({
  value,
  onChange,
}: {
  value?: string
  onChange: (value: string) => void
}) {
  const parts = parseParts(value) ?? DEFAULT_PARTS
  return (
    <div className="flex h-60 w-full gap-1 rounded-md bg-white p-1 shadow-popover">
      <TimeColumn
        items={HOUR_ITEMS}
        selected={String(parts.hour12)}
        onSelect={(v) =>
          onChange(composeValue({ ...parts, hour12: Number(v) }))
        }
      />
      <TimeColumn
        items={MINUTE_ITEMS}
        selected={String(parts.minute)}
        onSelect={(v) =>
          onChange(composeValue({ ...parts, minute: Number(v) }))
        }
      />
      <TimeColumn
        items={PERIOD_ITEMS}
        selected={parts.period}
        onSelect={(v) =>
          onChange(composeValue({ ...parts, period: v as Period }))
        }
      />
    </div>
  )
}

export function TimePicker({
  value,
  onChange,
  onOpenChange,
  placeholder,
  size = 'sm',
  error = false,
  className,
  portal,
}: TimePickerProps) {
  const label = useMemo(() => (value ? formatTimeLabel(value) : ''), [value])

  return (
    <Popover
      align="left"
      side="auto"
      portal={portal}
      onOpenChange={onOpenChange}
      wrapperClassName={cn('min-w-0', className)}
      contentClassName="min-w-full"
      trigger={(open) => (
        <div
          className={cn(
            'flex w-full cursor-pointer items-center gap-1.5 rounded-sm border bg-white transition-colors',
            SIZE_CLASSES[size],
            error
              ? 'border-red-500 focus-within:focus-ring-error'
              : 'border-gray-200 focus-within:focus-ring',
            open && !error && 'focus-ring',
          )}
        >
          <span className="min-w-0 flex-1 truncate text-left">
            {label ? (
              <span className="text-gray-900">{label}</span>
            ) : (
              <span className="text-gray-300">{placeholder}</span>
            )}
          </span>
          <ChevronDownIcon
            className={cn(
              'size-2.5 shrink-0 text-gray-500 transition-transform duration-200',
              open && 'rotate-180',
            )}
          />
        </div>
      )}
    >
      <TimePanel value={value} onChange={onChange} />
    </Popover>
  )
}
